import React, { createContext, useContext, useState, ReactNode } from 'react';
import { loginRequest, getCurrentUser } from '../utils/authApi';
import {
  setAuthToken,
  clearAuthToken,
  isTokenValid,
  getAuthToken,
  SESSION_EXPIRED_EVENT,
} from '../utils/apiClient';
import { identityFromCurrentUser, isSuperAdminIdentity } from '../utils/jwtIdentity';
import { clearViewingTenant } from '../utils/partnerViewContext';
import { getAccount } from '../utils/accountsApi';

export interface User {
  id: string;
  username: string;
  email: string;
  role: string;
  accountId?: string;
  roleId?: string;
  /** Set only when this user is scoped to one specific branch (their real
   * UserDto.branchId) — null/undefined means account-wide access, so any
   * page creating a branch-scoped record must let them choose one instead
   * of assuming a single branch. See src/hooks/useBranchSelection.ts. */
  branchId?: string | null;
  /** This user's own tenant/company name (from the Accounts API) — shown in
   * the sidebar/dashboard for a real login, as opposed to `viewingTenant`
   * which is exclusive to Control Center's SuperAdmin preview flow. */
  accountName?: string;
}

/** `viewTenantDashboard` (Control Center) fabricates identities with this ID
 * prefix and never issues a real token for them — only such an identity may
 * legitimately keep the `viewingTenant` flag (see partnerViewContext.ts). Any
 * real, directly-logged-in user must not inherit a preview flag left behind
 * by an earlier, unrelated preview session on the same browser tab. */
const PARTNER_VIEW_ID_PREFIX = 'partner-view-';
const isPartnerPreviewUser = (u: User | null): boolean =>
  !!u && u.id.startsWith(PARTNER_VIEW_ID_PREFIX);

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<User>;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);

  const login = async (email: string, password: string): Promise<User> => {
    const { token, expiresAt } = await loginRequest(email, password);
    setAuthToken(token, expiresAt);
    // Fetch the full profile from /auth/me rather than decoding the JWT —
    // the token itself never carries an accountId claim (see jwtIdentity.ts).
    const currentUser = await getCurrentUser();
    let resolvedUser: User = identityFromCurrentUser(currentUser);
    // SuperAdmin isn't scoped to one tenant's Accounts row and lands on
    // /control-center, not /admin, so there's no per-account name to show.
    if (!isSuperAdminIdentity(resolvedUser) && resolvedUser.accountId) {
      try {
        const account = await getAccount(Number(resolvedUser.accountId));
        resolvedUser = { ...resolvedUser, accountName: account.accountName };
      } catch {
        // Sidebar/DashboardPage fall back to generic branding if this fails.
      }
    }
    setUser(resolvedUser);
    localStorage.setItem('user', JSON.stringify(resolvedUser));
    // A real login is never a Control Center preview identity — clear any
    // stale viewingTenant flag so this session doesn't inherit a previous,
    // unrelated preview's tenant name/banner.
    clearViewingTenant();
    return resolvedUser;
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
    clearAuthToken();
    clearViewingTenant();
  };

  // Restore session on mount if a valid, unexpired token exists
  React.useEffect(() => {
    if (!isTokenValid()) {
      if (getAuthToken()) {
        logout();
      }
      return;
    }
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      const restored: User = JSON.parse(savedUser);
      setUser(restored);
      if (!isPartnerPreviewUser(restored)) {
        clearViewingTenant();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The API rejected the stored token mid-session (see apiFetch): end the
  // session here too, so ProtectedRoute sends the user to /login rather than
  // leaving them on a page where every call now fails.
  React.useEffect(() => {
    window.addEventListener(SESSION_EXPIRED_EVENT, logout);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, logout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value: AuthContextType = {
    user,
    login,
    logout,
    isAuthenticated: !!user,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
